import React, { useMemo, useState } from 'react';

const StaticPointRotator = ({ points, activeRouteIds, trayConfig, mapRef, onOpenPointPopup }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const safePoints = useMemo(() => {
    const arr = Array.isArray(points) ? points : [];
    const filtered = arr.filter((pt) => {
      if (!pt) return false;
      const lat = Number(pt.lat);
      const lng = Number(pt.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

      const corr = pt.cooresponding_route;
      if (corr == null) return true;

      const key = String(corr);
      if (!activeRouteIds) return true;
      if (Array.isArray(activeRouteIds)) return activeRouteIds.includes(key);
      return activeRouteIds.has(key);
    });

    return filtered;
  }, [points, activeRouteIds]);

  const showIcon = Boolean(trayConfig && trayConfig.showIcon);
  const showName = Boolean(trayConfig && trayConfig.showName);
  const onLoadMessage = (trayConfig && typeof trayConfig.on_load_message === 'string')
    ? trayConfig.on_load_message
    : '';
  const onLoadIcon = (trayConfig && typeof trayConfig.on_load_icon === 'string')
    ? trayConfig.on_load_icon
    : '';
  const startButtonLabel = (trayConfig && typeof trayConfig.start_button_label === 'string' && trayConfig.start_button_label.trim().length > 0)
    ? trayConfig.start_button_label.trim()
    : 'Start';

  const centerOnPoint = (pt) => {
    if (!pt || !mapRef || !mapRef.current) return;
    const lat = Number(pt.lat);
    const lng = Number(pt.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const map = mapRef.current;
    const focusZoom = 14;

    if (typeof map.setView === 'function') {
      map.setView([lat, lng], focusZoom, { animate: true, duration: 0.5 });
    }

    if (typeof onOpenPointPopup === 'function') {
      window.setTimeout(() => {
        onOpenPointPopup(pt);
      }, 350);
    }
  };

  const selectIndex = (idx) => {
    if (!safePoints.length) return;
    const next = ((idx % safePoints.length) + safePoints.length) % safePoints.length;
    setSelectedIndex(next);
    centerOnPoint(safePoints[next]);
  };

  const handlePrev = () => {
    if (!safePoints.length) return;
    if (selectedIndex == null) return selectIndex(safePoints.length - 1);
    return selectIndex(selectedIndex - 1);
  };

  const handleNext = () => {
    if (!safePoints.length) return;
    if (selectedIndex == null) return selectIndex(0);
    return selectIndex(selectedIndex + 1);
  };

  const selectedPoint = selectedIndex != null ? safePoints[selectedIndex] : null;
  const displayName = selectedPoint
    ? (selectedPoint.title || selectedPoint.name || selectedPoint.id || '')
    : (onLoadMessage || '');

  const emoji = selectedPoint && typeof selectedPoint['icon-emoji'] === 'string'
    ? selectedPoint['icon-emoji'].trim()
    : '';

  const handleStart = () => {
    if (!safePoints.length) return;
    selectIndex(0);
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      gap: '10px',
      paddingBottom: '10px',
      marginBottom: '10px',
      borderBottom: '1px solid #eee'
    }}>
      {selectedPoint == null ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            overflow: 'hidden'
          }}>
            {showIcon && onLoadIcon && (
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '14px',
                background: 'white',
                border: '1px solid #eee',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                lineHeight: 1,
                flex: '0 0 28px'
              }}>
                {onLoadIcon}
              </div>
            )}

            <div style={{
              fontSize: '14px',
              fontWeight: 700,
              color: '#333',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {displayName}
            </div>
          </div>

          <button
            type="button"
            onClick={handleStart}
            disabled={!safePoints.length}
            style={{
              height: '34px',
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #0d6efd',
              background: safePoints.length ? '#0d6efd' : '#8bb9ff',
              color: 'white',
              fontSize: '13px',
              fontWeight: 700,
              cursor: safePoints.length ? 'pointer' : 'not-allowed',
              flex: '0 0 auto'
            }}
          >
            {startButtonLabel}
          </button>
        </div>
      ) : (
        <>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            minHeight: '34px'
          }}>
            {showIcon && selectedPoint != null && (
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '14px',
                background: 'white',
                border: '1px solid #eee',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                lineHeight: 1
              }}>
                {emoji}
              </div>
            )}

            {showName && (
              <div style={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#333',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '70vw'
              }}>
                {displayName}
              </div>
            )}
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <button
              type="button"
              onClick={handlePrev}
              disabled={!safePoints.length}
              style={{
                height: '34px',
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #0d6efd',
                background: safePoints.length ? '#0d6efd' : '#8bb9ff',
                color: 'white',
                fontSize: '13px',
                fontWeight: 700,
                cursor: safePoints.length ? 'pointer' : 'not-allowed',
                flex: '0 0 auto'
              }}
              aria-label="Previous"
            >
              Previous
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={!safePoints.length}
              style={{
                height: '34px',
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #0d6efd',
                background: safePoints.length ? '#0d6efd' : '#8bb9ff',
                color: 'white',
                fontSize: '13px',
                fontWeight: 700,
                cursor: safePoints.length ? 'pointer' : 'not-allowed',
                flex: '0 0 auto'
              }}
              aria-label="Next"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default StaticPointRotator;
